import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Phone, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useSettings } from "@/context/SettingsContext";
import { Logo } from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About BJRC" },
  { to: "/age-groups", label: "Age Groups" },
  { to: "/coaches", label: "Our Coaches" },
  { to: "/staff", label: "Our Staff" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/gallery", label: "Gallery" },
  { to: "/news", label: "News & Events" },
  { to: "/contact", label: "Contact" },
];

const MORE_LINKS = [
  { to: "/rugby-nexus", label: "Rugby Nexus", desc: "Player development platform" },
  { to: "/untamed-gear", label: "Untamed Sports Gear", desc: "Official kit & equipment supplier" },
  { to: "/social-media", label: "Social Media", desc: "Follow the BJRC action" },
];

export const Navbar = () => {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `px-2.5 py-2 text-[13px] font-semibold uppercase tracking-wide transition-colors rounded-md ${
      isActive ? "text-[#1E63D6]" : "text-slate-700 hover:text-[#0B2A4A]"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200/70 bg-white/90 backdrop-blur-md">
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          <Logo className="h-12 w-12" settings={settings} />
          <div className="leading-none">
            <div className="font-heading text-xl font-extrabold uppercase tracking-tight text-[#0B2A4A]">
              Brackenfell
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Junior Rugby Club
            </div>
          </div>
        </Link>

        <div className="hidden items-center xl:flex">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} end={l.to === "/"} data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
          <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
            <button
              className="flex items-center gap-1 rounded-md px-2.5 py-2 text-[13px] font-semibold uppercase tracking-wide text-slate-700 transition-colors hover:text-[#0B2A4A]"
              data-testid="nav-more-trigger"
              onClick={() => setMoreOpen((v) => !v)}
            >
              More <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 top-full w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="nav-more-dropdown">
                {MORE_LINKS.map((m) => (
                  <NavLink
                    key={m.to}
                    to={m.to}
                    onClick={() => setMoreOpen(false)}
                    className="block border-b border-slate-100 px-4 py-3 last:border-0 transition-colors hover:bg-slate-50"
                    data-testid={`nav-more-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <div className="text-sm font-bold uppercase tracking-wide text-[#0B2A4A]">{m.label}</div>
                    <div className="text-xs text-slate-500">{m.desc}</div>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/join"
            data-testid="nav-join-bjrc-button"
            className="hidden rounded-full bg-[#E8A317] px-5 py-2.5 text-sm font-extrabold uppercase tracking-wide text-[#0A1626] shadow-sm transition-all hover:bg-[#CC8F12] active:scale-95 sm:inline-flex"
          >
            Join BJRC
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 text-[#0B2A4A] xl:hidden"
                data-testid="nav-mobile-trigger"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] overflow-y-auto bg-white p-0">
              <div className="flex items-center gap-3 border-b border-slate-200 p-5">
                <Logo className="h-11 w-11" settings={settings} />
                <span className="font-heading text-lg font-extrabold uppercase text-[#0B2A4A]">BJRC</span>
              </div>
              <div className="flex flex-col p-3">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `rounded-lg px-4 py-3 text-base font-semibold ${isActive ? "bg-[#0B2A4A] text-white" : "text-slate-800 hover:bg-slate-100"}`
                    }
                    data-testid={`nav-mobile-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {l.label}
                  </NavLink>
                ))}
                <div className="mt-2 px-4 pb-1 pt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">More</div>
                {MORE_LINKS.map((m) => (
                  <NavLink
                    key={m.to}
                    to={m.to}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `rounded-lg px-4 py-3 text-base font-semibold ${isActive ? "bg-[#0B2A4A] text-white" : "text-slate-800 hover:bg-slate-100"}`
                    }
                    data-testid={`nav-mobile-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {m.label}
                  </NavLink>
                ))}
                <Link
                  to="/join"
                  onClick={() => setOpen(false)}
                  className="mt-3 rounded-full bg-[#E8A317] px-5 py-3 text-center text-base font-extrabold uppercase text-[#0A1626]"
                  data-testid="nav-mobile-join"
                >
                  Join BJRC
                </Link>
                {settings?.phone_admin && (
                  <a href={`tel:${settings.phone_admin.replace(/\s/g, "")}`} className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg border border-[#0B2A4A] px-4 py-3 text-sm font-bold text-[#0B2A4A]">
                    <Phone className="h-4 w-4" /> Call the club
                  </a>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
